<template>
  <validation-provider :rules="rules" :name="name" v-bind="$attrs" v-slot="{ errors, valid, invalid, validated }">
    <b-form-group>
      <slot name="label">
        <label v-if="label" :class="labelClasses">
          {{ label }}{{ ' ' }}<span v-if="required" style="color:red">*</span>
        </label>
      </slot>
      <div :class="[
        { 'input-group': hasIcon },
        { focused: focused },
        { 'input-group-alternative': alternative },
        { 'has-label': label || $slots.label },
        inputGroupClasses
      ]">
        <div v-if="prependIcon || $slots.prepend" class="input-group-prepend">
          <span class="input-group-text">
            <slot name="prepend">
              <i :class="prependIcon"></i>
            </slot>
          </span>
        </div>
        <slot v-bind="slotData">
          <div v-if="type == 'read-only-price'" class="read-only-price">

            {{ formatCurrency(value) }}

          </div>
          <input ref="input" v-if="type != 'currency' && type != 'read-only-price'" :value="value" :type="type" v-on="listeners"
            v-bind="$attrs" :valid="valid" :required="required" class="form-control" :class="[
              { 'is-valid': valid && validated && successMessage },
              { 'is-invalid': invalid && validated },
              { 'is-capitalised': isCapitalised },
              inputClasses
            ]" />
          <input ref="input" v-if="type == 'currency'" :value="value" :type="type" v-on="listeners" v-bind="$attrs" :valid="valid"
            :required="required" class="form-control" :class="[
              { 'is-valid': valid && validated && successMessage },
              { 'is-invalid': invalid && validated },
              inputClasses
            ]" />
        </slot>
        <div v-if="appendIcon || $slots.append" class="input-group-append">
          <span class="input-group-text">
            <slot name="append">
              <i :class="appendIcon"></i>
            </slot>
          </span>
        </div>
        <slot name="infoBlock"></slot>
      </div>
      <slot name="success">
        <div class="valid-feedback" v-if="valid && validated && successMessage">
          {{ successMessage }}
        </div>
      </slot>
      <slot name="error">
        <div v-if="errors[0]" class="invalid-feedback" style="display: block;">
          {{ errors[0] }}
        </div>
      </slot>
    </b-form-group>
  </validation-provider>
</template>
<script>
import Vue from "vue";
import { validationMethods } from "../../helpers/validation";
import { ValidationProvider, extend } from "vee-validate";

// Validation extensions
extend("email", {
  validate: value => validationMethods.email(value),
  message: "Please provide a valid email"
});
extend("abn", {
  validate: value => validationMethods.simpleABN(value),
  message: "Please provide a valid ABN"
});
extend("date30DaysFromNow", {
  validate: value => validationMethods.dateWithin30Days(value),
  message:
    "The start date cannot be in the past and not more than 30 days in the future"
});

Vue.component("ValidationProvider", ValidationProvider);

export default {
  inheritAttrs: false,
  name: "base-input",
  props: {
    isCapitalised: false,
    required: {
      type: Boolean,
      description: "Whether input is required (adds an asterix *)"
    },
    group: {
      type: Boolean,
      description:
        "Whether input is an input group (manual override in special cases)"
    },
    alternative: {
      type: Boolean,
      description: "Whether input is of alternative layout"
    },
    label: {
      type: String,
      description: "Input label (text before input)"
    },
    error: {
      type: String,
      description: "Input error (below input)"
    },
    successMessage: {
      type: String,
      description: "Input success message",
      default: ""
    },
    labelClasses: {
      type: String,
      description: "Input label css classes",
      default: "form-control-label"
    },
    inputClasses: {
      type: String,
      description: "Input css classes"
    },
    inputGroupClasses: {
      type: String,
      description: "Input group css classes"
    },
    value: {
      type: [String, Number],
      description: "Input value"
    },
    type: {
      type: String,
      description: "Input type",
      default: "text"
    },
    appendIcon: {
      type: String,
      description: "Append icon (right)"
    },
    prependIcon: {
      type: String,
      description: "Prepend icon (left)"
    },
    rules: {
      type: [String, Array, Object],
      description: "Vee validate validation rules",
      default: ""
    },
    name: {
      type: String,
      description: "Input name (used for validation)",
      default: ""
    },
    preserveCursor: {
      type: Boolean,
      description: "Whether to preserve cursor position after controlled input updates",
      default: false
    },
    selectOnFocus: {
      type: Boolean,
      description: "Whether to select the whole input value when the input receives focus",
      default: false
    }
  },
  data() {
    return {
      focused: false,
      pendingSelection: null
    };
  },
  computed: {
    listeners() {
      return {
        ...this.$listeners,
        input: this.updateValue,
        focus: this.onFocus,
        blur: this.onBlur,
        keyup: this.onKeyUp
      };
    },
    slotData() {
      return {
        focused: this.focused,
        error: this.error,
        ...this.listeners
      };
    },
    hasIcon() {
      const { append, prepend } = this.$slots;
      return (
        append !== undefined ||
        prepend !== undefined ||
        this.appendIcon !== undefined ||
        this.prependIcon !== undefined ||
        this.group
      );
    }
  },
  methods: {
    formatCurrency(val) {

      const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2, // Ensures at least 2 decimal places (e.g., 1,200.00)
    maximumFractionDigits: 2  // Ensures at most 2 decimal places (e.g., 1,200.56)
});
      return formatter.format(parseFloat(val));
    },
    updateValue(evt) {
      let value = evt.target.value;
      const target = evt.target;
      const selectionStart = target.selectionStart;
      const selectionEnd = target.selectionEnd;
      if (this.preserveCursor && selectionStart !== null && selectionEnd !== null) {
        this.pendingSelection = {
          start: selectionStart,
          end: selectionEnd
        };
      }
      this.$emit("input", value);
    },
    restoreCursor() {
      if (!this.preserveCursor || !this.pendingSelection) {
        return;
      }

      const target = this.$refs.input;
      if (document.activeElement === target && target.setSelectionRange) {
        const valueLength = String(target.value || "").length;
        const start = Math.min(this.pendingSelection.start, valueLength);
        const end = Math.min(this.pendingSelection.end, valueLength);
        target.setSelectionRange(start, end);
      }
      this.pendingSelection = null;
    },
    onFocus(evt) {


      if (this.type == "currency") {


        // var target = evt.target;

        // var tmp = target.value.replace(/,/g, "");
        // var val = Number(tmp).toLocaleString("en-AU");

        // if (tmp == "") {
        //   //target.value = "";
        //   this.$emit("input", "");
        // } else {
        //   this.$emit("input", val);
        // }

        var target = evt.target;
        var val = target.value.replace(/[,.]/g, "");

        //evt.target.selectAll();



        this.$emit("input", val);
      }

      if (this.type == "number") {
        evt.target.select();
      }
      if (this.selectOnFocus && evt.target.select) {
        window.setTimeout(() => {
          if (document.activeElement === evt.target) {
            evt.target.select();
          }
        }, 0);
      }
      this.focused = true;
      this.$emit("focus", evt);
    },
    onKeyUp(evt) {
      this.$emit("keyup", evt);
    },
    onBlur(evt) {
      if (this.type == "currency") {
        var target = evt.target;

        var tmp = target.value.replace(/,/g, "");
        var val = Number(tmp).toLocaleString("en-AU");

        if (tmp == "") {
          //target.value = "";
          this.$emit("input", "");
        } else {
          this.$emit("input", val);
        }
      }

      this.focused = false;
      this.$emit("blur", evt);
    }
  },
  updated() {
    this.restoreCursor();
  }
};
</script>
<style>
.is-capitalised {
  text-transform: uppercase;
}

.read-only-price {
  display: block;


  padding: 0.625rem 0.75rem;

  font-weight: 500 !important;
  line-height: 1.5;

  background-color: #e9ecef;
  opacity: 1;
  background-clip: padding-box;
  border: 1px solid #dee2e6;
  border-radius: 0 0.25rem 0.25rem 0;
  font-size: 0.875rem;
  transition: all 0.15s ease-in-out;
  height: calc(1.5em + 1.25rem + 5px);
  position: relative;
  flex: 1 1 auto;
  width: 1%;
  margin-bottom: 0;



}
</style>
